import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, message } from "antd";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../hooks";
import { ReviEvaluationsApi } from "../../../../api/revi/ReviEvaluationsApi";

const evalApi = new ReviEvaluationsApi();

// `value` es el DATO que se guarda en BD (no se traduce nunca) y `labelKey` es
// la clave del texto que ve el terapeuta. Separarlos es lo que permite traducir
// la lista sin tocar lo que ya hay guardado en las evaluaciones existentes.
export const CLINICAL_AREAS = [
  { value: "Cognitiva", labelKey: "cognitiva" },
  { value: "Física", labelKey: "fisica" },
  { value: "Emocional", labelKey: "emocional" },
  { value: "Funcional", labelKey: "funcional" },
  { value: "General", labelKey: "general" },
];

// Solo la clave tecnica: el titulo y la descripcion de cada plantilla viven en
// evaluations.json, bajo asistente.plantillas.<key>.
export const TEMPLATE_OPTIONS = [
  { key: "yes_no" },
  { key: "rating_1_3" },
  { key: "rating_1_5" },
  { key: "input_score" },
];

export function normalizeVisibility(raw) {
  const v = (raw || "").toString().toLowerCase();
  if (v === "private" || v === "public" || v === "global") return v;
  return "private";
}

export function normalizeSubtype(raw) {
  const v = (raw || "").toString();
  const exists = TEMPLATE_OPTIONS.some((t) => t.key === v);
  return exists ? v : "rating_1_5";
}

export function canSelectGlobal(role) {
  return role === "admin" || role === "superadmin" || role === "partner_admin";
}

export function toFormInitial(initialData) {
  if (!initialData) {
    return {
      subtype: "rating_1_5",
      clinicalArea: "General",
      title: "",
      description: "",
      questions: [{ id: "", text: "", reverse: false }],
      visibility: "private",
    };
  }

  const subtype = normalizeSubtype(initialData.subtype);
  const clinicalArea = initialData.clinicalArea || "General";
  const title = initialData.title || "";
  const description = initialData.description || "";
  const visibility = normalizeVisibility(initialData.visibility);

  const questions =
    Array.isArray(initialData.questions) && initialData.questions.length > 0
      ? initialData.questions.map((q, idx) => ({
          id: q?.id || "",
          text: q?.text || "",
          reverse: Boolean(q?.reverse),
          order: q?.order ?? idx + 1,
        }))
      : [{ id: "", text: "", reverse: false }];

  return { subtype, clinicalArea, title, description, questions, visibility };
}

export function buildPayload(values) {
  const now = Date.now();
  const questionsRaw = Array.isArray(values.questions) ? values.questions : [];

  const questions = questionsRaw
    .map((q, idx) => ({
      id: q?.id || `q_${now}_${idx}`,
      text: (q?.text || "").toString().trim(),
      reverse: Boolean(q?.reverse),
      order: idx + 1,
    }))
    .filter((q) => q.text.length > 0);

  return {
    subtype: values.subtype,
    clinicalArea: values.clinicalArea,
    title: (values.title || "").toString().trim(),
    description: (values.description || "").toString().trim(),
    questions,
    visibility: normalizeVisibility(values.visibility),
  };
}

export function useEvaluationWizard({
  isOpen,
  initialData,
  onClose,
  onSuccess,
}) {
  const { t } = useTranslation("evaluations");
  const { accessToken, user } = useAuth();
  const role = useMemo(() => user?.role || user?.user?.role || "", [user]);

  const isEditing = Boolean(initialData && initialData._id);
  const isStandard = initialData?.type === "standardized";

  const [form] = Form.useForm();
  const subtype = Form.useWatch("subtype", form);
  const visibility = Form.useWatch("visibility", form);

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // anti-race: si se cierra mientras submit
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setStep(0);
    form.setFieldsValue(toFormInitial(initialData));
  }, [isOpen, initialData, form]);

  const stepItems = useMemo(
    () => [
      { title: t("asistente.pasos.tipoArea") },
      { title: t("asistente.pasos.identidad") },
      { title: t("asistente.pasos.preguntas") },
      { title: t("asistente.pasos.compartir") },
    ],
    [t],
  );

  const validateStep = useCallback(async () => {
    if (step === 0) {
      await form.validateFields(["subtype", "clinicalArea"]);
      return;
    }
    if (step === 1) {
      await form.validateFields(["title", "description"]);
      return;
    }
    if (step === 2) {
      await form.validateFields(["questions"]);
      const values = form.getFieldsValue(true);
      const payload = buildPayload(values);
      if (!payload.questions || payload.questions.length === 0) {
        message.error(t("asistente.mensajes.faltanPreguntas"));
        throw new Error("No questions");
      }
      return;
    }
    if (step === 3) {
      await form.validateFields(["visibility"]);
      return;
    }
  }, [form, step, t]);

  const next = useCallback(async () => {
    try {
      await validateStep();
      setStep((s) => Math.min(s + 1, 3));
    } catch {
      // AntD ya marca campos. Además, si faltan preguntas, ya mostramos message.
    }
  }, [validateStep]);

  const prev = useCallback(() => setStep((s) => Math.max(s - 1, 0)), []);

  const pickTemplate = useCallback(
    (key) => {
      form.setFieldsValue({ subtype: key });
      const qs = form.getFieldValue("questions");
      if (!Array.isArray(qs) || qs.length === 0) {
        form.setFieldsValue({
          questions: [{ id: "", text: "", reverse: false }],
        });
      }
    },
    [form],
  );

  const moveQuestion = useCallback(
    (from, to) => {
      const qs = form.getFieldValue("questions") || [];
      if (!Array.isArray(qs)) return;
      if (to < 0 || to >= qs.length) return;
      const copy = [...qs];
      const [item] = copy.splice(from, 1);
      copy.splice(to, 0, item);
      form.setFieldsValue({ questions: copy });
    },
    [form],
  );

  const submit = useCallback(async () => {
    if (!accessToken) {
      message.error(t("asistente.mensajes.sesionInvalida"));
      return;
    }
    if (isStandard) {
      message.error(t("asistente.mensajes.estandarNoEditable"));
      return;
    }

    setSubmitting(true);
    try {
      // valida todo el wizard
      await form.validateFields([
        "subtype",
        "clinicalArea",
        "title",
        "description",
        "questions",
        "visibility",
      ]);

      const values = form.getFieldsValue(true);
      const payload = buildPayload(values);

      // guardas
      if (!payload.title || payload.title.length < 3) {
        message.error(t("asistente.mensajes.tituloCorto"));
        return;
      }
      if (!payload.questions || payload.questions.length === 0) {
        message.error(t("asistente.mensajes.faltanPreguntas"));
        return;
      }
      if (payload.visibility === "global" && !canSelectGlobal(role)) {
        payload.visibility = "public"; // fallback seguro
      }

      if (isEditing) {
        await evalApi.updateCustomEvaluation(
          accessToken,
          initialData._id,
          payload,
        );
        message.success(t("asistente.mensajes.actualizada"));
      } else {
        await evalApi.createCustomEvaluation(accessToken, payload);
        message.success(t("asistente.mensajes.creada"));
      }

      if (!aliveRef.current) return;
      onSuccess?.();
      onClose?.();
    } catch (e) {
      // e.errorFields => validación de AntD (no es crash)
      if (e?.errorFields) {
        message.error(t("asistente.mensajes.revisaCampos"));
        return;
      }
      console.error(e);
      message.error(e?.message || t("asistente.mensajes.errorGuardar"));
    } finally {
      if (aliveRef.current) setSubmitting(false);
    }
  }, [
    accessToken,
    form,
    initialData,
    isEditing,
    isStandard,
    onClose,
    onSuccess,
    role,
    t,
  ]);

  return {
    form,
    step,
    setStep,
    stepItems,
    submitting,

    subtype: subtype || "rating_1_5",
    visibility: visibility || "private",

    isEditing,
    isStandard,
    role,

    next,
    prev,
    submit,

    pickTemplate,
    moveQuestion,
  };
}
